import type { CSSProperties } from 'react';

/** Figma `Popover` 세트(Components, 35:550)의 축 */
export const POPOVER_PLACEMENTS = ['top', 'bottom', 'left', 'right'] as const;
export type PopoverPlacement = (typeof POPOVER_PLACEMENTS)[number];

export const POPOVER_ARROWS = ['sm', 'lg'] as const;
export type PopoverArrow = (typeof POPOVER_ARROWS)[number];

export const POPOVER_STATUSES = ['default', 'init'] as const;
export type PopoverStatus = (typeof POPOVER_STATUSES)[number];

export const POPOVER_FRAMES = ['default', 'none'] as const;
/**
 * 상자 틀. default = Figma Popover 틀, none = 틀 없음(면 · 테두리 · 여백 없이 내용만) —
 * 내용이 제 테두리를 가진 경우(DatePicker 달력 · TimePicker 열 묶음). 테두리가 두 겹이 되지 않게 한다(DD-23 6)
 */
export type PopoverFrame = (typeof POPOVER_FRAMES)[number];

/** default: 흰 면 · 2px border/control · 모서리 20 · 안쪽 10 · 그림자 없음. 너비는 내용에 맞춘다 */
export const POPOVER_SURFACE_CLASS: Record<PopoverFrame, string> = {
  default:
    'flex flex-col gap-2 rounded-4xl border-2 border-border-control bg-bg-surface p-2-5 text-fg-default',
  none: 'flex flex-col text-fg-default',
};

/** 상자와 꼭지를 늘어놓는 방향(DOM 순서는 상자 → 꼭지). top = 기준 요소 위에 뜨고 꼭지가 아래 */
export const POPOVER_LAYOUT_CLASS: Record<PopoverPlacement, string> = {
  top: 'flex-col',
  bottom: 'flex-col-reverse',
  left: 'flex-row',
  right: 'flex-row-reverse',
};

interface ArrowShape {
  width: number;
  height: number;
  /** 닫힌 모양(sm, 칠함) 또는 열린 V 선(lg, 선만) */
  d: string;
  /**
   * lg: V 안쪽 면(상자 테두리 2px 까지 내려와 그 구간의 테두리를 덮는다) — 꼭지와 상자 사이에 선이 없는 말풍선 모양
   */
  fill?: string;
  /** 상자 테두리와 겹치는 쪽 여백(lg 는 -2 로 테두리 위에 놓는다). 토큰 밖 값이라 인라인 style */
  style?: CSSProperties;
}

/**
 * 꼭지. sm = 16×8 칠한 삼각형(테두리 없음 — Figma 그대로라 흰 바탕에서는 보이지 않는다),
 * lg = 보이는 크기 40×15 의 말풍선 꼭지 — 알림 목록(2026-10-07 사용자: 꼭지와 상자 사이에 선이 없게).
 * 상자 테두리(2px)만큼 더 그려(17) 테두리 위에 겹치고, 안쪽 면(fill)이 그 구간의 테두리를 덮고,
 * V 선은 상자 테두리와 같은 2px border/control 로 테두리 가운데에서 끝난다 — 상자와 꼭지가 한 윤곽이 된다.
 */
export const POPOVER_ARROW: Record<PopoverArrow, Record<PopoverPlacement, ArrowShape>> = {
  sm: {
    top: { width: 16, height: 8, d: 'M0 0 L16 0 L8 8 Z' },
    bottom: { width: 16, height: 8, d: 'M0 8 L16 8 L8 0 Z' },
    left: { width: 8, height: 16, d: 'M0 0 L0 16 L8 8 Z' },
    right: { width: 8, height: 16, d: 'M8 0 L8 16 L0 8 Z' },
  },
  lg: {
    top: {
      width: 40,
      height: 17,
      d: 'M1 1 L20 16 L39 1',
      fill: 'M1 1 L20 16 L39 1 L39 0 L1 0 Z',
      style: { marginTop: -2 },
    },
    bottom: {
      width: 40,
      height: 17,
      d: 'M1 16 L20 1 L39 16',
      fill: 'M1 16 L20 1 L39 16 L39 17 L1 17 Z',
      style: { marginBottom: -2 },
    },
    left: {
      width: 17,
      height: 40,
      d: 'M1 1 L16 20 L1 39',
      fill: 'M1 1 L16 20 L1 39 L0 39 L0 1 Z',
      style: { marginLeft: -2 },
    },
    right: {
      width: 17,
      height: 40,
      d: 'M16 1 L1 20 L16 39',
      fill: 'M16 1 L1 20 L16 39 L17 39 L17 1 Z',
      style: { marginRight: -2 },
    },
  },
};

/** lg 는 relative — 상자(위치 없음)보다 위에 그려져 테두리를 덮는다 */
export const POPOVER_ARROW_CLASS: Record<PopoverArrow, string> = {
  sm: 'shrink-0 fill-bg-surface',
  lg: 'relative shrink-0 overflow-visible',
};

/** lg 꼭지의 면 · 선(선 굵기 = 상자 테두리 2) */
export const POPOVER_ARROW_LG_FILL_CLASS = 'fill-bg-surface';
export const POPOVER_ARROW_LG_STROKE_CLASS = 'fill-none stroke-border-control';
export const POPOVER_ARROW_STROKE_WIDTH = 2;

/** inline 으로 기준 요소 옆에 붙일 때의 자리 */
export const POPOVER_INLINE_POSITION_CLASS: Record<PopoverPlacement, string> = {
  top: 'bottom-full left-1/2 mb-1 -translate-x-1/2',
  bottom: 'top-full left-1/2 mt-1 -translate-x-1/2',
  left: 'top-1/2 right-full mr-1 -translate-y-1/2',
  right: 'top-1/2 left-full ml-1 -translate-y-1/2',
};

/** portal 로 띄울 때 기준 요소와의 간격(px, space/1) */
export const POPOVER_OFFSET_PX = 4;

/**
 * status=init 의 막대 — 내용 자리 너비 200(`size.skeleton.200`, DD-19)에 12 높이 3줄(200 · 200 · 120).
 */
export const POPOVER_SKELETON_WIDTH_CLASS = 'w-(--size-skeleton-200)';
